import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getPopularComics, getLatestComics, getTopRatedComics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function StatItem({ icon, value, label }: { icon: string; value: string; label: string }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
        <i className={`${icon} text-primary-600 text-lg`}></i>
      </div>
      <div>
        <div className="text-xl font-bold text-foreground-900 font-heading">{value}</div>
        <div className="text-sm text-foreground-500">{label}</div>
      </div>
    </div>
  );
}

function HeroComicCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group relative overflow-hidden rounded-xl bg-background-100 aspect-[4/5] block"
    >
      <img
        src={comic.cover}
        alt={comic.title}
        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-accent-500 text-foreground-950">
          {comic.status}
        </span>
        <h3 className="text-base font-bold text-background-50 mt-2 font-heading">{comic.title}</h3>
        <p className="text-xs text-background-50/70 mt-1 line-clamp-2">{comic.description}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-accent-400 flex items-center gap-1">
            <i className="ri-star-fill text-[10px]"></i>
            {comic.rating}
          </span>
          <span className="text-xs text-background-50/50">
            {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万次阅读` : `${comic.views}次阅读`}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const popularComics = getPopularComics(8);
  const latestComics = getLatestComics(8);
  const topRatedComics = getTopRatedComics(6);
  const heroComics = popularComics.slice(0, 3);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setFeaturedIndex((prev) => (prev + 1) % heroComics.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroComics.length]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] flex items-center overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20stylized%20abstract%20background%20with%20warm%20coral%20and%20gold%20gradients%2C%20soft%20flowing%20shapes%2C%20modern%20minimal%20design%2C%20subtle%20comic%20book%20texture%2C%20elegant%20and%20artistic%2C%20no%20text%2C%20smooth%20gradient%20transitions%2C%20warm%20atmospheric%20lighting&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/30 to-background-50" />
          </div>

          <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              {/* Left: Text */}
              <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <span className="inline-block px-3 py-1 rounded-full bg-primary-500/20 text-primary-400 text-xs font-medium mb-4">
                  🔥 热门无删减韩漫在线阅读
                </span>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 font-heading leading-tight">
                  最新高清免费<br />
                  <span className="text-accent-400">韩国漫画大全</span>
                </h1>
                <p className="mt-4 text-base md:text-lg text-background-50/70 max-w-lg leading-relaxed">
                  歪歪漫画网汇集最新高清免费韩国漫画大全，主打热门无删减韩漫在线阅读，为您呈现原汁原味的韩国漫画作品库。
                </p>
                <div className="flex items-center gap-4 mt-6 flex-wrap">
                  <Link
                    to="/category/romance"
                    className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    开始阅读
                  </Link>
                  <Link
                    to="/about"
                    className="px-6 py-3 rounded-full border border-background-50/30 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap"
                  >
                    了解更多
                  </Link>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 mt-10">
                  <StatItem icon="ri-book-open-line" value="500+" label="漫画资源" />
                  <StatItem icon="ri-user-line" value="120万+" label="活跃读者" />
                  <StatItem icon="ri-thumb-up-line" value="9.4" label="平均评分" />
                </div>
              </div>

              {/* Right: Featured Comics */}
              <div className="hidden lg:grid grid-cols-3 gap-4">
                {heroComics.map((comic, idx) => (
                  <div
                    key={comic.id}
                    className={`transition-all duration-500 ${
                      idx === featuredIndex ? 'scale-105 z-10' : 'scale-95 opacity-70'
                    }`}
                    onMouseEnter={() => setFeaturedIndex(idx)}
                  >
                    <HeroComicCard comic={comic} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Quick Nav */}
        <section className="w-full bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 -mt-8 relative z-20">
            <div className="bg-background-50 rounded-xl p-4 shadow-sm border border-background-200/70">
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-background-100 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-primary-600`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Popular Comics */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最受欢迎的韩国漫画作品</p>
              </div>
              <Link
                to="/category/romance"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {popularComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Banner */}
        <section className="w-full py-12 bg-primary-500">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading">
              每日更新 · 永不书荒
            </h2>
            <p className="mt-2 text-background-50/80 text-sm">
              每天都有新鲜韩漫上架，追更不等待，精彩不错过
            </p>
            <div className="flex items-center justify-center gap-3 mt-6 flex-wrap">
              {['恋爱', '动作', '奇幻', '悬疑', '校园'].map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${tag === '恋爱' ? 'romance' : tag === '动作' ? 'action' : tag === '奇幻' ? 'fantasy' : tag === '悬疑' ? 'thriller' : 'school'}`}
                  className="px-5 py-2 rounded-full bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors whitespace-nowrap"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Top Rated */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                高分神作
              </h2>
              <p className="text-sm text-foreground-500 mt-1">读者评分最高的韩国漫画</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {topRatedComics.map((comic, idx) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group relative"
                >
                  <div className="relative overflow-hidden rounded-lg bg-background-50 aspect-[3/4]">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-0 left-0 w-8 h-8 bg-accent-500 text-foreground-950 flex items-center justify-center rounded-br-lg font-bold text-sm font-heading">
                      {idx + 1}
                    </div>
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-foreground-800 text-center line-clamp-1 font-heading">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-400 text-center mt-0.5">
                    <i className="ri-star-fill text-accent-500 text-[10px]"></i> {comic.rating}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  最近更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">最新上架的韩国漫画章节</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {latestComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="large" />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-1">关于歪歪漫画网的常见疑问解答</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                { q: '歪歪漫画网的漫画都是免费的吗？', a: '是的，歪歪漫画网所有韩国漫画资源均免费提供在线阅读，无需付费即可畅享最新高清韩漫。' },
                { q: '漫画更新频率是怎样的？', a: '我们与多家韩漫版权方合作，热门连载漫画通常每周更新1-2话，确保读者能够及时追更。' },
                { q: '可以在手机上阅读吗？', a: '当然可以！歪歪漫画网支持手机、平板和电脑等多端阅读，自适应屏幕尺寸，随时随地畅享韩漫。' },
                { q: '漫画是否支持离线下载？', a: '目前我们提供在线高清阅读服务，离线下载功能正在开发中，敬请期待。' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70">
                  <summary className="flex items-center justify-between p-4 cursor-pointer font-medium text-foreground-800 text-sm">
                    {faq.q}
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                  </summary>
                  <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="w-full py-14 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="bg-background-100 rounded-2xl p-8 md:p-12 text-center max-w-2xl mx-auto">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-mail-send-line text-primary-600 text-xl"></i>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                订阅更新通知
              </h2>
              <p className="text-sm text-foreground-500 mt-2 mb-6">
                第一时间获取最新韩漫上架通知和热门推荐
              </p>
              <form
                data-readdy-form=""
                action="https://readdy.ai/api/form/d9pev0di2vhsdh2d32vg"
                method="POST"
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value.trim();
                  if (honeypot) {
                    const msg = document.getElementById('newsletter-msg');
                    if (msg) { msg.textContent = '订阅成功！感谢您的关注。'; msg.className = 'text-sm text-green-600 mt-3'; }
                    return;
                  }
                  const formData = new FormData(form);
                  fetch('https://readdy.ai/api/form/d9pev0di2vhsdh2d32vg', { method: 'POST', body: formData })
                    .then(async (res) => {
                      const t = await res.text();
                      let parsed;
                      try { parsed = JSON.parse(t); } catch { parsed = null; }
                      const serverMsg = parsed?.meta?.message || parsed?.message || '';
                      const msg = document.getElementById('newsletter-msg');
                      if (res.ok && parsed?.code === 'OK') {
                        if (msg) { msg.textContent = '订阅成功！感谢您的关注。'; msg.className = 'text-sm text-green-600 mt-3'; }
                        form.reset();
                      } else {
                        if (msg) { msg.textContent = serverMsg || '订阅失败，请稍后重试'; msg.className = 'text-sm text-red-500 mt-3'; }
                      }
                    })
                    .catch(() => {
                      const msg = document.getElementById('newsletter-msg');
                      if (msg) { msg.textContent = '订阅成功！感谢您的关注。'; msg.className = 'text-sm text-green-600 mt-3'; }
                      form.reset();
                    });
                }}
              >
                <input
                  type="email"
                  name="email"
                  placeholder="请输入您的邮箱地址"
                  required
                  className="flex-1 px-4 py-3 rounded-full bg-background-50 border border-background-200/70 text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                />
                <button
                  type="submit"
                  className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  立即订阅
                </button>
                <div className="sp-field--website_alt">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
              </form>
              <p id="newsletter-msg" className="text-sm text-green-600 mt-3"></p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}